import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  DataListItem,
  Highlight,
  PagingSidePanelType,
  SidePanel,
} from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { ConditionalTooltip } from 'components/Conditional/ConditionalTooltip'
import { SidePanelHeaderTemplateThree } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateThree'
import { usePendingAgreementsContext } from 'hooks/context/usePendingAgreementsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { highlightRowBasedOnId } from 'utils/table/table'
import { LegalAgreementDetailLink } from './LegalAgreementDetailLink'
import { LegalAgreementStatus, LegalAgreementTableType } from './types'
import { getAgreementDisplayDate, getIsAgreementAssignedToUser } from './utils'

export type LegalAgreementDetailsProps = {
  isOpen: boolean
  agreement: LegalAgreementTableType
  pageAgreements: PagingSidePanelType
  onClose: () => void
}

export const LegalAgreementDetails = ({
  isOpen,
  agreement,
  pageAgreements,
  onClose,
}: LegalAgreementDetailsProps) => {
  const { t } = useTranslation()
  const { profile } = useProfileContext()
  const { setShouldRefetch, setShowSingleAgreementId } = usePendingAgreementsContext()
  const bannerSpacing = useComponentHeight(appBannersId)

  const agreementDetails = agreement?.agreement
  const { privacyPolicyName, privacyPolicyUrl } = agreementDetails || {}

  const isSigned = agreement?.status === LegalAgreementStatus.SIGNED

  const receivedDate = useMemo(
    () => getAgreementDisplayDate(agreement?.dateCreated),
    [agreement?.dateCreated]
  )

  const acceptedDate = useMemo(() => {
    if (!isSigned) return ''
    return getAgreementDisplayDate(agreement?.lastUpdated)
  }, [isSigned, agreement?.lastUpdated])

  const appendix = useMemo(() => agreement?.appendixes?.[0], [agreement?.appendixes])

  const isAssignedToUser = useMemo(
    () => getIsAgreementAssignedToUser(agreement, profile),
    [agreement, profile]
  )

  return (
    <SidePanel
      id="legal-agreements-side-panel"
      isOpen={isOpen}
      onClose={onClose}
      paging={pageAgreements}
      activeRowId={agreementDetails?.reference}
      onActiveRowChange={highlightRowBasedOnId}
      externalStyle={{ top: `${bannerSpacing}px` }}
      header={
        <SidePanelHeaderTemplateThree
          heading={agreementDetails?.displayName}
          description={agreementDetails?.description}
          bottomContent={{
            leftLabel: (
              <Box>
                <Highlight type={agreement?.statusPill?.state}>
                  {agreement?.statusPill?.label as string}
                </Highlight>
              </Box>
            ),
          }}
        />
      }
      body={
        <Box flexCol width="full">
          <DataListItem id="received" title={t('legalAgreements.received')} body={receivedDate} />
          {isSigned && (
            <DataListItem id="accepted" title={t('legalAgreements.accepted')} body={acceptedDate} />
          )}
          <DataListItem
            id="assignedTo"
            title={t('assignedTo')}
            body={agreement?.user?.emailAddress ?? ''}
          />

          {isSigned ? (
            <>
              <LegalAgreementDetailLink
                id="agreementLink"
                title={agreementDetails?.displayName}
                filename={agreementDetails?.fileName}
                url={agreementDetails?.url}
              />

              {privacyPolicyName && privacyPolicyUrl && (
                <LegalAgreementDetailLink
                  id="privacyPolicyLink"
                  title={privacyPolicyName}
                  filename={privacyPolicyName}
                  url={privacyPolicyUrl}
                />
              )}
              {!!appendix && (
                <LegalAgreementDetailLink
                  id="appendixLink"
                  title={t('legalAgreements.appendixLinkTitle')}
                  tooltipText={t('legalAgreements.appendixLinkTooltip')}
                  filename={appendix?.name}
                  url={appendix?.url}
                />
              )}
            </>
          ) : (
            <ConditionalTooltip
              isEnabled={!isAssignedToUser}
              bodyContent={t('legalAgreements.assignedToOtherUser')}
            >
              <Button
                disabled={!isAssignedToUser}
                className="mt-4"
                onClick={() => {
                  setShowSingleAgreementId(agreement?.id)
                  setShouldRefetch(true)
                  onClose()
                }}
              >
                {t('reviewAndAccept')}
              </Button>
            </ConditionalTooltip>
          )}
        </Box>
      }
    />
  )
}
